import {executeThunk} from '@Utils/thunk.util';
import {getBusinessProfile} from '@Components/business-profiles-wizard/business-profiles-wizard-thunk';
import {useEffect} from 'react';
import type {LoadingStates} from '@Utils/loading.util';
import {fetchResourcesIfNotLoaded, isLoading} from '@Utils/loading.util';
import type {BusinessHoursMap, BusinessProfile, BusinessProfileCategory, BusinessProfileType} from '@Components/business-profiles-wizard/business-profiles-wizard.types';
import {BusinessProfileState, BusinessProfilesWizardMode, BusinessProfileWizardStep} from '@Components/business-profiles-wizard/business-profiles-wizard.types';
import type {SocialMediaProfile} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {initSocialMediaProfileLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {getAllBrandsWithAssets} from '@Components/mystuff-brands/brands-thunk';
import type {BrandKitWithAssets} from '@Components/mystuff-brands/brand-kits.types';
import {useSortedBrandKitsWithAssets} from '@Hooks/brands/useSortedBrandKitsWithAssets';
import {joinBusinessProfileDisplayableAddress} from '@Libraries/business-profiles-wizard-library';
import {useHasContentForAllBrandKitsLoaded} from '@Hooks/brands/useHasContentForAllBrandKitsLoaded';
import {setBrandsLoadingState} from '@Components/mystuff-brands/brands-reducer';
import {BRANDS_LOADING_STATES, canUserAccessBrandKits} from '@Libraries/brands-library';
import type {BusinessProfilesActivityLogs} from '@Panels/business-profiles-activity-panel/business-profiles-activity-panel.types';
import type {ReduxThunkReturnType} from '@/store';
import {useAppDispatch, useAppSelector} from '@/hooks';

export const useLoadingState = (): LoadingStates => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.loadingState;
  });
};

export const useBusinessProfile = (): BusinessProfile | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile;
  });
};

export const useSavedBusinessProfile = (): BusinessProfile | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.savedBusinessProfile;
  });
};

export const useBusinessProfileState = (): BusinessProfileState => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.state ?? BusinessProfileState.USER_CREATED_UNSAVED;
  });
};

export const useWizardMode = (): BusinessProfilesWizardMode => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.mode;
  });
};

export const useBusinessProfileName = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.name ?? '';
  });
};

export const useCurrentStep = (): BusinessProfileWizardStep => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.currentStep;
  });
};

export const useBusinessProfileWebsite = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.website ?? '';
  });
};

export const useBusinessProfileContactEmail = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.contactEmail ?? '';
  });
};

export const useBusinessProfileAutoFilledEmail = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.autoFilledEmail ?? '';
  });
};

export const useBusinessProfilePhoneNumber = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.phoneNumber ?? '';
  });
};

export const useBusinessProfileTypes = (): Partial<Record<BusinessProfileType, string>> | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.types ?? undefined;
  });
};

export const useBusinessHours = (): BusinessHoursMap | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.businessHours ?? undefined;
  });
};

export const useInitBusinessProfile = (): void => {
  const dispatch = useAppDispatch();
  const hasBrandKitContentLoaded = useHasContentForAllBrandKitsLoaded();
  const loadingState = useLoadingState();

  useEffect(() => {
    void fetchResourcesIfNotLoaded(loadingState, () => {
      return executeThunk((): Promise<ReduxThunkReturnType> => {
        return dispatch(getBusinessProfile());
      });
    });

    if (!hasBrandKitContentLoaded && canUserAccessBrandKits()) {
      void dispatch(getAllBrandsWithAssets());
    } else {
      dispatch(setBrandsLoadingState(BRANDS_LOADING_STATES.LOADED));
    }
  }, []);
};

export const useBusinessProfilesSocialMediaLinks = (): Record<SocialMediaProfile, string> => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.socialMediaProfileLinks ?? initSocialMediaProfileLinks();
  });
};

export const useBusinessAddressCountry = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.address?.country ?? '';
  });
};

export const useBusinessAddressStreetAddress = (): string[] => {
  const address = useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.address?.address;
  });

  return address ?? [''];
};

export const useBusinessAddressSector = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.address?.sector ?? '';
  });
};

export const useBusinessAddressCity = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.address?.city ?? '';
  });
};

export const useBusinessAddressPostalCode = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.address?.postalCode ?? '';
  });
};

export const useDisplayableBusinessHours = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.displayableBusinessHours ?? '';
  });
};

export const useBusinessProfileBrandID = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.idBrand ?? '';
  });
};

export const useSelectedCategoryTags = (): Record<string, string> => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.categories ?? {};
  });
};

export const useBusinessProfileBrandKit = (): BrandKitWithAssets | undefined => {
  const businessProfileBrandID = useBusinessProfileBrandID();
  const brandKitsWithAssets: BrandKitWithAssets[] = useSortedBrandKitsWithAssets();

  if (businessProfileBrandID) {
    return brandKitsWithAssets.find((brandKit) => {
      return brandKit.brandKit.brandId === businessProfileBrandID;
    });
  }
  return undefined;
};

export const useGoogleMapsAPIKey = (): string => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.googleMapAPIKey;
  });
};

export const useAllBusinessProfiles = (): BusinessProfile[] | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.allBusinessProfiles;
  });
};

export const useBusinessProfileDisplayableAddress = (): string => {
  const businessAddressCountry = useBusinessAddressCountry();
  const businessAddressStreetAddress = useBusinessAddressStreetAddress();
  const businessAddressSector = useBusinessAddressSector();
  const businessAddressCity = useBusinessAddressCity();
  const businessAddressPostalCode = useBusinessAddressPostalCode();

  return joinBusinessProfileDisplayableAddress(businessAddressStreetAddress, businessAddressSector, businessAddressCity, businessAddressPostalCode, businessAddressCountry);
};

export const useBusinessProfileGoogleAccountId = (): string | undefined => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.businessProfile?.googleLocationId ?? undefined;
  });
};

export const useIsFieldsErrorState = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.isFieldsErrorState ?? false;
  });
};

export const useIsContentLoading = (): boolean => {
  const hasBrandKitsContentLoaded = useHasContentForAllBrandKitsLoaded();

  return useAppSelector(({businessProfilesWizard}) => {
    return isLoading(businessProfilesWizard.loadingState) || !hasBrandKitsContentLoaded;
  });
};

export const useIsImportingProfile = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.isImportingProfile ?? false;
  });
};

export const useIsImportedProfile = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.isImportedProfile ?? false;
  });
};

export const useActivityLogs = (): BusinessProfilesActivityLogs[] => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.activityLogs ?? [];
  });
};

export const useAllCategories = (): Record<string, BusinessProfileCategory> => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.allCategories;
  });
};

export const useIsProfileOverwriting = (): boolean => {
  const businessProfileName = useBusinessProfileName();
  const businessProfileTypes = useBusinessProfileTypes();
  const businessProfileCategories = useSelectedCategoryTags();
  const displayableBusinessHours = useDisplayableBusinessHours();
  const businessProfileWebsite = useBusinessProfileWebsite();

  return (
    businessProfileName !== '' ||
    (businessProfileTypes && Object.values(businessProfileTypes).length > 0) ||
    (businessProfileCategories && Object.values(businessProfileCategories).length > 0) ||
    displayableBusinessHours !== '' ||
    businessProfileWebsite !== ''
  );
};

export const useIsDataPreviewPanel = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.currentStep === BusinessProfileWizardStep.LAST || businessProfilesWizard.mode === BusinessProfilesWizardMode.SAVED;
  });
};

export const useCurrentWebsites = (): string[] => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.currentWebsites ?? [];
  });
};

export const useIsBrandFromWebsiteProcessed = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.isBrandFromWebsiteProcessed ?? false;
  });
};

export const useDoesBrandNeedToBeCreated = (): boolean => {
  const currentWebsites = useCurrentWebsites();
  const businessProfileWebsite = useBusinessProfileWebsite();
  const idBrandKit = useBusinessProfileBrandID();
  return !idBrandKit && !currentWebsites?.includes(businessProfileWebsite);
};

export const useHasUserSeenUpsellingBanner = (): boolean => {
  return useAppSelector(({businessProfilesWizard}) => {
    return businessProfilesWizard.hasUserSeenUpsellingBanner ?? false;
  });
};
